import React, { useState, useEffect, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import TopBar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import DataTable from "../../../../../../component/DataTable";
import { claimColumns } from "../claimColumns";
import BtnSecondary from '../../../../../../component/SecondaryButton';
import { SearchInput, FilterSelect } from "../../../../../../component/listing";
import { getAllExpense } from '../../../../../../services/expense';
import { getEmployeeDetails } from '../../../../../../utils/getEmployeeNames';
import { useNotify } from '../../../../../../component/NotificationProvider';

const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

const ExpenseRequests = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { state } = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Use rows handed in via navigation state if present, else fetch fresh.
  useEffect(() => {
    if (Array.isArray(state?.data) && state.data.length) {
      setRows(state.data);
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await getAllExpense();
        if (res?.success === false) throw new Error(res?.message || "Failed to load expenses");
        const list = (res.data || []).filter((item) => item.type === "ANY");
        const combined = await getEmployeeDetails(list, list.map((d) => d.submitterId), "submitterId");
        if (!active) return;
        setRows((combined || []).map((item) => ({
          _id: item._id,
          date: new Date(item.createdAt).toLocaleDateString("en-GB"),
          employeeName: item.employee ? `${item.employee.fname || ""} ${item.employee.lname || ""}`.trim() : "Unknown",
          employee: item.employee,
          category: item?.policyCateogary?.name || "—",
          amount: Number(item.amount || 0),
          status: item.status,
        })));
      } catch (err) {
        if (active) { setError(true); notify.error(err?.message || "Failed to load expenses"); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesStatus = statusFilter === "all" || r.status === statusFilter;
      const matchesSearch = !q || (r.employeeName || "").toLowerCase().includes(q) || (r.category || "").toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [rows, search, statusFilter]);

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Expenses"} breadcrumbs={[{ label: "Expenses", href: "" }, { label: "Requests", href: "" }]} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee or category" />
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              options={[{ value: "all", label: "All statuses" }, { value: "pending", label: "Pending" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }]} />
          </Stack>
          <BtnSecondary to="/app/expense-dashboard" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>

        <DataTable
          rows={visible}
          loading={loading}
          error={error}
          errorTitle="Couldn't load expenses"
          empty={{ title: "No expense claims", description: "Nothing matches the current filter." }}
          onRowClick={(r) => navigate(`/app/expense-requests-details/${r._id}`)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={claimColumns(money)}
        />
      </MainBox>
    </Box>
  );
};

export default ExpenseRequests;
